import type { NavEntry, NavMode, NavSection } from './nav.types'

/**
 * The entry whose path is the longest prefix of the current pathname.
 *
 * Longest prefix, so `/settings` does not shadow `/settings/sso`; segment-aware, so
 * `/settingsfoo` is not a match for `/settings`. Both matter in this codebase: "Abonnement"
 * lives at `/n/:slug/settings/subscription` and would otherwise be swallowed by "Général".
 *
 * This replaces the rail's previous rule — strict `pathname === to` — under which no entry
 * ever lit up on a deep page. Standing on a webhook's delivery log, nothing in the rail said
 * where you were.
 */
export const resolveActiveEntry = (
  sections: NavSection[],
  pathname: string
): NavEntry | undefined => {
  let best: NavEntry | undefined

  for (const section of sections) {
    for (const entry of section.entries) {
      const matches =
        pathname === entry.to || pathname.startsWith(`${entry.to}/`)

      if (matches && (!best || entry.to.length > best.to.length)) {
        best = entry
      }
    }
  }

  return best
}

/** The account pages that belong to the settings rail rather than the work rail. */
const USER_SETTINGS_PATHS = ['/my/profile', '/my/preferences']

/**
 * Which rail this URL belongs to.
 *
 * A path segment equal to `settings` puts us in the settings mode — that is true of
 * `/n/:slug/settings`, `/c/:slug/settings/media` and everything below them. Compared segment
 * by segment rather than with `includes`, so a future `/my/settings-guide` is not mistaken for
 * a settings page.
 *
 * The user's own two account screens are the exception: they are settings by nature but do not
 * carry the segment, and moving them would break every existing link.
 */
export const resolveNavMode = (pathname: string): NavMode => {
  if (USER_SETTINGS_PATHS.includes(pathname)) {
    return 'settings'
  }

  return pathname.split('/').includes('settings') ? 'settings' : 'work'
}

/**
 * The space a URL describes, read from the address bar rather than from the stored context.
 *
 * The two disagree more often than one would like: the `(workspace)` route groups never align
 * `contextIri` to the URL, so a deep link to `/n/a/materials` while the stored context points
 * at network *b* is an ordinary situation. The address bar is what the person asked for, so it
 * decides which rail they get; the stored context only answers when the URL says nothing —
 * everything under `/my`.
 */
export const resolveNavSpace = (
  pathname: string
): { kind: 'network' | 'community' | 'user'; slug?: string } => {
  const network = /^\/n\/([^/]+)/.exec(pathname)
  if (network) {
    return { kind: 'network', slug: network[1] }
  }

  const community = /^\/c\/([^/]+)/.exec(pathname)
  if (community) {
    return { kind: 'community', slug: community[1] }
  }

  return { kind: 'user' }
}
